import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, PencilLine } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAdsWorkspaceStore } from "@/features/ads";
import { formatSessionTimestamp } from "@/shared/lib/uiMessageUtils";

interface SessionDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function SessionDrawer({ open, onClose }: SessionDrawerProps) {
  const controls = useAdsWorkspaceStore((s) => s.companionSessionControls);
  if (!controls) return null;

  const {
    sessions,
    activeSessionId,
    isResolving,
    isCreating,
    isRenaming,
    onSwitchSession,
    onCreateSession,
    onRenameSession,
  } = controls;
  const isBusy = isResolving || isCreating;
  const handleCreateSession = async () => {
    await onCreateSession();
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/30 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          {/* Drawer */}
          <motion.div
            className="absolute bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-xl rounded-t-2xl border-t border-border/30 shadow-2xl max-h-[min(60vh,calc(100dvh-64px-env(safe-area-inset-bottom)))] flex flex-col"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
          >
            {/* Handle + header */}
            <div className="flex flex-col items-center pt-2 pb-1 shrink-0">
              <div className="w-8 h-1 rounded-full bg-muted-foreground/20 mb-2" />
              <div className="w-full flex items-center justify-between px-4 pb-2">
                <span className="text-xs font-semibold text-foreground/80 uppercase tracking-wider">
                  Marcus chats
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => void onRenameSession()}
                    disabled={isBusy || isRenaming || !activeSessionId}
                    className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground/60 hover:bg-muted/40 hover:text-foreground disabled:opacity-50 transition-colors"
                    title="Rename"
                    aria-label="Rename active chat"
                  >
                    <PencilLine className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleCreateSession()}
                    disabled={isBusy}
                    className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground/60 hover:bg-muted/40 hover:text-foreground disabled:opacity-50 transition-colors"
                    title="New Chat"
                    aria-label="Start new Marcus chat"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground/60 hover:bg-muted/40 hover:text-foreground transition-colors"
                    aria-label="Close chat history"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Session list */}
            <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-[calc(1rem+env(safe-area-inset-bottom))] ds-scrollbar">
              {sessions.length === 0 ? (
                <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                  No chat sessions yet
                </div>
              ) : (
                sessions.map((session) => (
                  <button
                    key={session.id}
                    type="button"
                    onClick={() => {
                      onSwitchSession(session.id);
                      onClose();
                    }}
                    className={cn(
                      "w-full flex items-center justify-between py-2.5 px-3 rounded-lg text-left transition-colors",
                      session.id === activeSessionId
                        ? "bg-primary/10 text-primary"
                        : "hover:bg-muted/30 text-foreground/80",
                    )}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {session.id === activeSessionId && (
                        <div className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                      )}
                      <span className={cn("text-xs truncate", session.id === activeSessionId && "font-semibold")}>
                        {session.name}
                      </span>
                    </div>
                    <span className="ml-2 shrink-0 text-[10px] text-muted-foreground/50 tabular-nums">
                      {formatSessionTimestamp(session.createdAt)}
                    </span>
                  </button>
                ))
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
